import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/i18n/LanguageContext";

const BASE_URL = "https://envir.com";
const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/Oq93RtoWCGW14hVbnwfUpEi28i42/social-images/social-1772628383532-envir.webp";

type Tri = { no: string; en: string; de: string };

interface SEOProps {
  title: Tri | string;
  description: Tri | string;
  path: string;
  image?: string;
  type?: "website" | "article";
  jsonLd?: object | object[];
  noindex?: boolean;
}

const pick = (v: Tri | string, lang: "no" | "en" | "de") =>
  typeof v === "string" ? v : v[lang] || v.no;

const SEO = ({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
}: SEOProps) => {
  const { language } = useLanguage();
  const t = pick(title, language);
  const d = pick(description, language);
  const url = `${BASE_URL}${path}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={language} />
      <title>{t}</title>
      <meta name="description" content={d} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Envir" />
      <meta property="og:locale" content={language === "no" ? "nb_NO" : language === "de" ? "de_DE" : "en_US"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
export { BASE_URL };
